// src/features/secureDocuments/types/index.ts

// ============================================
// SECURE CUSTOMER UPLOADS
// ============================================

/**
 * Ein Upload = Eine Versand-Aktion mit mehreren Dateien
 * Wird auf der Download-Seite als "Block" angezeigt
 */
export interface SecureCustomerUpload {
  id: string;
  customer_id: string;
  contact_id: string | null; // NEU: Referenz auf customer_contacts - isolierter Downloadbereich pro Kontakt
  
  // Metadaten
  titel: string;              // "Datei Upload von Tim Wolter am 30.01.2025"
  kommentar: string | null;   // Individueller Kommentar für E-Mail
  
  // Versand
  versand_status: 'entwurf' | 'geplant' | 'versendet' | 'fehler';
  versendet_am: string | null;
  fehler_nachricht: string | null;

  // Terminierter Versand + Außenidentität „Senden als" (Stufe 2)
  /** Geplanter Versandzeitpunkt (UTC); gesetzt bei versand_status='geplant'. */
  geplanter_versand_am: string | null;
  /**
   * Gewählter Absender (Außenidentität): bestimmt den kundensichtbaren Titel
   * sowie Signatur/Absendername/Duz-Gate der Versand-Mail und wird vom
   * Versand-Cron (secure-scheduled-send) für geplante Pakete verwendet.
   * erstellt_von_* bleibt die tatsächlich handelnde Person (Audit).
   */
  sender_email: string | null;
  /** Claim-Guard des Versand-Crons; solange gesetzt, ist Abbrechen gesperrt. */
  versand_claimed_at: string | null;

  // Veralten-Tracking (Punkt 2 Monatsabschlusspaket): Periode + Paket-Typ
  // bilden den Gruppierungsschlüssel; ein neu versendetes Paket markiert
  // frühere Pakete derselben Gruppe via DB-Trigger als veraltet. NULL bei
  // Paketen ohne natürlichen Vorgänger (z. B. manuelles "Sonstiges").
  periode_jahr: number | null;
  periode_monat: number | null;
  paket_typ: string | null;
  veraltet_am: string | null;
  ersetzt_durch_upload_id: string | null;

  // Folgepaket (Schritt 2 Monatsabschlusspaket): gesetzt, wenn dieses Paket
  // ein durch einen neuen DATEV-Stand überholtes Paket ersetzt.
  folgepaket_von_upload_id?: string | null;
  folgepaket_aenderungen?: FolgepaketAenderung[] | null;
  
  // Empfänger (kopiert zum Zeitpunkt des Versands)
  empfaenger_email: string | null;
  empfaenger_cc: string | null;
  
  // Audit
  erstellt_von_email: string;
  erstellt_von_name: string;
  created_at: string;
  updated_at: string;
}

/**
 * Folgepaket: eine geänderte Unterlage gegenüber dem ersetzten Paket.
 * personalnummern bei Mitarbeiter-Bündeln, kassen bei Beitragsnachweisen.
 */
export interface FolgepaketAenderung {
  dokument_typ: string;
  titel: string;
  art: 'geaendert' | 'neu';
  personalnummern?: number[];
  kassen?: string[];
}

/**
 * Eine einzelne Datei innerhalb eines Uploads
 */
export interface SecureCustomerUploadFile {
  id: string;
  upload_id: string;
  
  original_filename: string;
  storage_path: string;
  file_size: number;
  file_type: 'pdf' | 'doc' | 'docx' | 'xlsx' | 'xls' | 'txt' | 'csv' | 'jpg' | 'jpeg' | 'png' | 'xml' | 'zip';
  sort_order: number;
  
  created_at: string;
}

/**
 * Upload mit zugehörigen Dateien (für Anzeige)
 */
export interface SecureUploadWithFiles extends SecureCustomerUpload {
  files: SecureCustomerUploadFile[];
}

// ============================================
// VERSAND-LOG
// ============================================

export interface SecureCustomerSendLog {
  id: string;
  upload_id: string;
  
  email_empfaenger: string;
  email_cc: string | null;
  email_betreff: string;
  versand_erfolg: boolean;
  fehler_details: string | null;
  versendet_am: string;
}

// ============================================
// INPUT TYPES (für Erstellen/Aktualisieren)
// ============================================

export interface SecureUploadCreateInput {
  customer_id: string;
  contact_id: string; // NEU: Pflichtfeld - jeder Upload gehört zu einem spezifischen Ansprechpartner
  titel: string;
  kommentar: string | null;
  erstellt_von_email: string;
  erstellt_von_name: string;
  /** Optional (Stufe 2): Außenidentität „Senden als" — E-Mail des gewählten Absenders. */
  sender_email?: string | null;
  /**
   * Optional (Stufe 2): terminierter Versand — UTC-Zeitpunkt. Wenn gesetzt,
   * wird der Upload mit versand_status='geplant' angelegt und vom Cron
   * (secure-scheduled-send) zum Zeitpunkt versendet.
   */
  geplanter_versand_am?: string | null;
  /**
   * Optional (Punkt 2): Veralten-Tracking. Periode + Paket-Typ bilden den
   * Gruppierungsschlüssel für die automatische Veraltet-Markierung via
   * DB-Trigger. Ohne diese Felder verhält sich der Insert wie bisher.
   */
  periode_jahr?: number | null;
  periode_monat?: number | null;
  paket_typ?: string | null;
}

export interface SecureUploadFileCreateInput {
  upload_id: string;
  original_filename: string;
  storage_path: string;
  file_size: number;
  file_type: SecureCustomerUploadFile['file_type'];
  sort_order?: number;
}

// ============================================
// MODAL & UI TYPES
// ============================================

/**
 * Datei im Upload-Modal (vor dem Hochladen)
 */
export interface PendingUploadFile {
  file: File;
  id: string;        // Temporäre ID für UI
  filename: string;
  size: number;
  type: string;
  isValid: boolean;
  error?: string;
}

/**
 * Props für das Upload-Modal
 */
export interface SecureDocumentModalProps {
  isOpen: boolean;
  customer: {
    id: string;
    company: string;
    email: string;
    email_cc?: string;
    secure_download_token?: string;
    secure_download_passwort?: string;
    // Für Anrede
    geschlecht?: string;
    anrede?: string;
    vorname: string | null;
    nachname: string | null;
  };
  onClose: () => void;
  onSuccess: () => void;
}

// ============================================
// PUBLIC PAGE TYPES
// ============================================

/**
 * Daten für die öffentliche Download-Seite
 */
export interface SecureDownloadPageData {
  customer: {
    id: string;
    company: string;
  };
  uploads: SecureUploadWithFiles[];
  tokenAblaufdatum: string;
}

/**
 * Request für Token-Validierung
 */
export interface SecureTokenValidationRequest {
  token: string;
  passwort: string;
}

/**
 * Response für Token-Validierung
 */
export interface SecureTokenValidationResult {
  valid: boolean;
  data?: SecureDownloadPageData;
  error?: string;
}

// ============================================
// SERVICE RESULT TYPES
// ============================================

export interface SecureUploadResult {
  success: boolean;
  upload?: SecureCustomerUpload;
  error?: string;
}

export interface SecureSendResult {
  success: boolean;
  messageId?: string;
  error?: string;
}

// ============================================
// ALLOWED FILE TYPES
// ============================================

export const ALLOWED_FILE_TYPES = [
  'pdf', 'doc', 'docx', 'xlsx', 'xls', 'csv', 'txt', 'jpg', 'jpeg', 'png', 'xml', 'zip'
] as const;

export const ALLOWED_MIME_TYPES: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'text/plain': 'txt',
  'text/csv': 'csv',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'application/xml': 'xml',
  'text/xml': 'xml',
  'application/zip': 'zip',
  'application/x-zip-compressed': 'zip'
};

export const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2 GB pro Datei
export const MAX_TOTAL_SIZE = 2 * 1024 * 1024 * 1024; // 2 GB gesamt pro Upload

/**
 * Schwelle für den Hinweis auf große Dateien im Upload-Modal. Dateien
 * oberhalb dieser Größe sind weiterhin gültig; der Upload läuft aber als
 * ein einziger Vorgang ohne Wiederaufnahme und dauert entsprechend lange.
 */
export const LARGE_FILE_WARN_SIZE = 25 * 1024 * 1024; // 25 MB
